import type { MultipleChoiceStaticContent } from '@core-api/editor/types/response';
import type { Question } from '@mentimeter/legacy-series-types/question';
import type {
  CompatibilityInteractiveContent,
  CompatibilitySlide,
} from '../../compatibility-types';
import {
  RESPONSE_POLICY_NO_RESTRICTION,
  RESPONSE_POLICY_SINGLE_RESPONSE_LIMITED_CHOICES,
} from '../../constants';
import { responseCountDisplayModeToQuestionPercentDefault } from '../converters/interactive-content';
import { handleNoInteractiveContentError } from '../../applications/common/handle-no-interactive-content-error';
import type { CommonQuestionProperty } from './get-common-question-property';

type InteractiveContentStylingSchema =
  MultipleChoiceStaticContent['interactiveContentStyling'];

export const getQuestionChoicesProperty = (
  target: CompatibilitySlide<'multiple-choice'>,
  prop: keyof Question,
) => {
  const interactiveContent = target.interactiveContents[0];
  const staticContent = target.staticContent;

  if (prop === 'question_styled') {
    return staticContent.styledTitle ?? {};
  }

  if (interactiveContent) {
    switch (prop as keyof Omit<Question, CommonQuestionProperty>) {
      case 'max_votes':
        return getMaxVotes(interactiveContent);
      case 'percent_default':
        return getQuestionResponseCountDisplayMode(
          staticContent.interactiveContentStyling,
        );
      default:
        return undefined;
    }
  }

  return handleNoInteractiveContentError(target, prop);
};

const getMaxVotes = (interactiveContent: CompatibilityInteractiveContent) => {
  switch (interactiveContent.responsePolicy) {
    case RESPONSE_POLICY_NO_RESTRICTION:
      return -1;
    case RESPONSE_POLICY_SINGLE_RESPONSE_LIMITED_CHOICES:
      return interactiveContent.maxEntries || -1;
    default:
      return 1;
  }
};

const getQuestionResponseCountDisplayMode = (
  interactiveContentStyling: InteractiveContentStylingSchema,
) => {
  if (interactiveContentStyling) {
    return responseCountDisplayModeToQuestionPercentDefault(
      interactiveContentStyling?.responseCountDisplayMode,
    );
  }
  return false;
};
